import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
    Package, Clock, CheckCircle, Truck, MapPin, 
    ChevronRight, Loader2, ArrowLeft, PartyPopper,
    AlertCircle, XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { apiRequest, formatDate, formatPrice } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const ORDER_STATUS_CONFIG = {
    pending: { 
        label: 'En attente', 
        icon: Clock, 
        color: 'text-amber-500', 
        bgColor: 'bg-amber-500/10',
        borderColor: 'border-amber-500/30'
    },
    confirmed: { 
        label: 'Confirmée', 
        icon: CheckCircle, 
        color: 'text-blue-500', 
        bgColor: 'bg-blue-500/10',
        borderColor: 'border-blue-500/30'
    },
    preparing: { 
        label: 'En préparation', 
        icon: Package, 
        color: 'text-purple-500', 
        bgColor: 'bg-purple-500/10',
        borderColor: 'border-purple-500/30'
    },
    ready: { 
        label: 'Prête', 
        icon: CheckCircle, 
        color: 'text-emerald-500', 
        bgColor: 'bg-emerald-500/10',
        borderColor: 'border-emerald-500/30'
    },
    shipped: { 
        label: 'Expédiée', 
        icon: Truck, 
        color: 'text-cyan-500', 
        bgColor: 'bg-cyan-500/10',
        borderColor: 'border-cyan-500/30'
    },
    delivered: { 
        label: 'Livrée', 
        icon: PartyPopper, 
        color: 'text-green-500', 
        bgColor: 'bg-green-500/10',
        borderColor: 'border-green-500/30'
    },
    cancelled: { 
        label: 'Annulée', 
        icon: XCircle, 
        color: 'text-red-500', 
        bgColor: 'bg-red-500/10',
        borderColor: 'border-red-500/30'
    }
};

const STATUS_STEPS = ['confirmed', 'preparing', 'ready', 'delivered'];
const DELIVERY_STATUS_STEPS = ['confirmed', 'preparing', 'shipped', 'delivered'];

function OrderStatusTimeline({ order }) {
    const currentStatus = order.status;
    const isDelivery = order.delivery_type === 'delivery';
    const steps = isDelivery ? DELIVERY_STATUS_STEPS : STATUS_STEPS;
    const currentIndex = steps.indexOf(currentStatus);
    
    if (currentStatus === 'cancelled' || currentStatus === 'pending') {
        return null;
    }
    
    return (
        <div className="relative py-4">
            <div className="flex justify-between items-center">
                {steps.map((step, index) => {
                    const config = ORDER_STATUS_CONFIG[step];
                    const Icon = config.icon;
                    const isCompleted = index <= currentIndex;
                    const isCurrent = index === currentIndex;
                    
                    return (
                        <div key={step} className="flex flex-col items-center relative z-10">
                            <div className={`
                                w-10 h-10 rounded-full flex items-center justify-center
                                transition-all duration-300
                                ${isCompleted ? config.bgColor + ' ' + config.color : 'bg-muted text-muted-foreground'}
                                ${isCurrent ? 'ring-2 ring-offset-2 ring-primary scale-110' : ''}
                            `}>
                                <Icon className="h-5 w-5" />
                            </div>
                            <span className={`
                                text-xs mt-2 font-medium
                                ${isCompleted ? config.color : 'text-muted-foreground'}
                            `}>
                                {config.label}
                            </span>
                        </div>
                    );
                })}
            </div>
            {/* Progress bar */}
            <div className="absolute top-[28px] left-[20px] right-[20px] h-1 bg-muted rounded-full">
                <div 
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${(currentIndex / (steps.length - 1)) * 100}%` }}
                />
            </div>
        </div>
    );
}

export default function OrderTrackingPage() {
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const [searchParams] = useSearchParams();
    const [orders, setOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    const orderId = searchParams.get('order_id');

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            navigate('/auth');
            return;
        }
        loadOrders();
    }, [user, authLoading]);

    useEffect(() => {
        if (orderId && orders.length > 0) {
            const order = orders.find(o => o.order_id === orderId);
            if (order) {
                setSelectedOrder(order);
            }
        }
    }, [orderId, orders]);

    const loadOrders = async () => {
        setLoading(true);
        try {
            const data = await apiRequest('/orders');
            setOrders(data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const getStatusConfig = (status) => {
        return ORDER_STATUS_CONFIG[status] || ORDER_STATUS_CONFIG.pending;
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 py-8" data-testid="order-tracking-page">
            <div className="container max-w-4xl mx-auto px-4">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => selectedOrder ? setSelectedOrder(null) : navigate(-1)}
                        className="rounded-full"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-heading font-bold">
                            {selectedOrder ? 'Détail de commande' : 'Mes commandes'}
                        </h1>
                        <p className="text-muted-foreground">
                            {selectedOrder ? `Commande #${selectedOrder.order_id}` : 'Suivez vos commandes en temps réel'}
                        </p>
                    </div>
                </div>

                {selectedOrder ? (
                    /* Order Detail View */
                    <div className="space-y-6">
                        {/* Status Card */}
                        <Card className={`glass rounded-2xl ${getStatusConfig(selectedOrder.status).borderColor} border-2`}>
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        {React.createElement(getStatusConfig(selectedOrder.status).icon, {
                                            className: `h-8 w-8 ${getStatusConfig(selectedOrder.status).color}`
                                        })}
                                        <div>
                                            <p className="text-sm text-muted-foreground">Statut actuel</p>
                                            <p className={`text-xl font-bold ${getStatusConfig(selectedOrder.status).color}`}>
                                                {getStatusConfig(selectedOrder.status).label}
                                            </p>
                                        </div>
                                    </div>
                                    <Badge variant="outline">{formatDate(selectedOrder.created_at)}</Badge>
                                </div>
                                
                                <OrderStatusTimeline order={selectedOrder} />
                            </CardContent>
                        </Card>

                        {/* Order Items */}
                        <Card className="glass rounded-2xl">
                            <CardHeader>
                                <CardTitle>Articles commandés</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {selectedOrder.items?.map((item, index) => (
                                    <div key={index} className="flex items-center gap-4 p-3 rounded-xl bg-muted/50">
                                        <div className="h-16 w-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                                            {item.image_url ? (
                                                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Package className="h-6 w-6 text-muted-foreground" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-medium">{item.name}</p>
                                            <p className="text-sm text-muted-foreground">Quantité: {item.quantity}</p>
                                        </div>
                                        <p className="font-mono font-semibold">{formatPrice(item.total)}</p>
                                    </div>
                                ))}
                                
                                <div className="border-t pt-4 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Sous-total</span>
                                        <span>{formatPrice(selectedOrder.subtotal || selectedOrder.total)}</span>
                                    </div>
                                    {selectedOrder.points_discount > 0 && (
                                        <div className="flex justify-between text-sm text-amber-500">
                                            <span>Réduction fidélité</span>
                                            <span>-{formatPrice(selectedOrder.points_discount)}</span>
                                        </div>
                                    )}
                                    {selectedOrder.delivery_fee > 0 && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">Livraison</span>
                                            <span>{formatPrice(selectedOrder.delivery_fee)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                                        <span>Total</span>
                                        <span className="text-primary">{formatPrice(selectedOrder.total)}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Delivery Info */}
                        <Card className="glass rounded-2xl">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <MapPin className="h-5 w-5" />
                                    {selectedOrder.delivery_type === 'delivery' ? 'Livraison' : 'Retrait en magasin'}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {selectedOrder.delivery_type === 'delivery' ? (
                                    <div>
                                        <p>{selectedOrder.delivery_address?.street}</p>
                                        <p>{selectedOrder.delivery_address?.postal_code} {selectedOrder.delivery_address?.city}</p>
                                        <p>{selectedOrder.delivery_address?.country}</p>
                                    </div>
                                ) : (
                                    <div>
                                        <p className="font-semibold">O.C.A Piscine</p>
                                        <p>5 place de la République</p>
                                        <p>83780 Flayosc</p>
                                        <p className="mt-2 text-sm text-muted-foreground">
                                            Lun-Sam: 9h-12h / 14h-18h
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Status History */}
                        {selectedOrder.status_history?.length > 0 && (
                            <Card className="glass rounded-2xl">
                                <CardHeader>
                                    <CardTitle>Historique</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        {selectedOrder.status_history.map((entry, index) => (
                                            <div key={index} className="flex items-start gap-3">
                                                <div className={`w-2 h-2 rounded-full mt-2 ${getStatusConfig(entry.to_status).bgColor.replace('/10', '')}`} />
                                                <div>
                                                    <p className="font-medium">
                                                        {getStatusConfig(entry.to_status).label}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {formatDate(entry.changed_at)}
                                                    </p>
                                                    {entry.message && (
                                                        <p className="text-sm mt-1 text-muted-foreground italic">
                                                            "{entry.message}"
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                ) : (
                    /* Orders List View */
                    <div className="space-y-4">
                        {orders.length === 0 ? (
                            <Card className="glass rounded-2xl">
                                <CardContent className="py-12 text-center">
                                    <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                    <h3 className="font-semibold text-lg mb-2">Aucune commande</h3>
                                    <p className="text-muted-foreground mb-4">
                                        Vous n&apos;avez pas encore passé de commande.
                                    </p>
                                    <Button onClick={() => navigate('/shop')} className="rounded-full">
                                        Découvrir nos produits
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            orders.map(order => {
                                const statusConfig = getStatusConfig(order.status);
                                const StatusIcon = statusConfig.icon;
                                
                                return (
                                    <Card 
                                        key={order.order_id}
                                        className="glass rounded-2xl hover:shadow-lg transition-shadow cursor-pointer"
                                        onClick={() => setSelectedOrder(order)}
                                        data-testid={`order-${order.order_id}`}
                                    >
                                        <CardContent className="p-4 sm:p-6">
                                            <div className="flex items-center justify-between gap-4">
                                                <div className="flex items-center gap-4">
                                                    <div className={`h-12 w-12 rounded-full ${statusConfig.bgColor} flex items-center justify-center`}>
                                                        <StatusIcon className={`h-6 w-6 ${statusConfig.color}`} />
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <p className="font-semibold">#{order.order_id}</p>
                                                            <Badge className={statusConfig.bgColor + ' ' + statusConfig.color}>
                                                                {statusConfig.label}
                                                            </Badge>
                                                        </div>
                                                        <p className="text-sm text-muted-foreground">
                                                            {formatDate(order.created_at)}
                                                        </p>
                                                        <p className="text-sm">
                                                            {order.items?.length || 0} article(s) • <span className="font-semibold">{formatPrice(order.total)}</span>
                                                        </p>
                                                    </div>
                                                </div>
                                                <ChevronRight className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
